import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { TimesheetDay } from "domain/entities/timesheet-day";
import { TimesheetDayInputFilter } from "domain/models/timesheet-day-input-filter";
import { Logger } from "../../infra/logger/logger";
import timesheetDayRepository from "infra/repositories/timesheet-day-repository";

/**
 * Hook customizado para buscar uma lista de registos diários de uma timesheet.
 * Utiliza o `useQuery` do `react-query` para gerir o estado do servidor, cache e refetching.
 *
 * @param {TimesheetDayInputFilter} filter - O objeto de filtro para a query.
 * @returns O resultado da query do `react-query`, contendo `data`, `isLoading`, `isError`, etc.
 */
export const useTimesheetDaysByFilter = (filter: TimesheetDayInputFilter, enabled: boolean = true) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["timesheetDays", filter],
    queryFn: async () => {
      try {
        const result = await timesheetDayRepository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          const errorMessage =
            error.response?.data?.metadata?.errors?.[0] ||
            "An unexpected error occurred";
          logger.error(errorMessage, error.response?.data?.metadata);
          throw new Error(errorMessage);
        }
        logger.error("Unknown error while fetching timesheet days", {
          error,
        });
        throw error;
      }
    },
    enabled,
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: 0,
  });
};

/**
 * Hook customizado para buscar um registo diário de timesheet por ID.
 *
 * @param {string} [id] - O ID do registo a ser buscado.
 * @returns O resultado da query do `react-query`.
 */
export const useTimesheetDaysById = (id?: string) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["timesheetDay", id],
    queryFn: async () => {
      try {
        if (!id) throw new Error("TimesheetDay ID inválido");
        const result = await timesheetDayRepository.getById(id);

        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          const errorMessage =
            error.response?.data?.metadata?.errors?.[0] ||
            "An unexpected error occurred";
          logger.error(errorMessage, error.response?.data?.metadata);
          throw new Error(errorMessage);
        }
        logger.error("Unknown error while fetching timesheet day by ID", {
          error,
        });
        throw error;
      }
    },
    enabled: !!id,
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
  });
};

/**
 * Hook customizado para criar um novo registo diário de timesheet.
 * Retorna uma função `mutateAsync` que, quando chamada, executa a criação.
 *
 * @returns A função `mutateAsync` do `react-query`.
 */
export const useCreateTimesheetDayMutation = () => {
  const { mutateAsync } = useMutation({
    mutationFn: (data: TimesheetDay) => timesheetDayRepository.create(data),
  });
  return mutateAsync;
};

/**
 * Hook customizado para criar múltiplos registos diários de timesheet de uma só vez (bulk).
 * Retorna uma função `mutateAsync` que, quando chamada, executa a criação em massa.
 *
 * @returns A função `mutateAsync` do `react-query`.
 */
export const useCreateAllTimesheetDayMutation = () => {
  const { mutateAsync } = useMutation({
    mutationFn: (data: TimesheetDay[]) =>
      timesheetDayRepository.createAll(data),
  });
  return mutateAsync;
};

/**
 * Hook customizado para atualizar um registo diário de timesheet existente.
 * Retorna uma função `mutateAsync` que, quando chamada, executa a atualização.
 *
 * @returns A função `mutateAsync` do `react-query`.
 */
export const useUpdateTimesheetDayMutation = () => {
  const { mutateAsync } = useMutation({
    mutationFn: (data: TimesheetDay) => timesheetDayRepository.update(data),
  });
  return mutateAsync;
};

/**
 * Hook customizado para apagar um registo diário de timesheet.
 * Retorna uma função `mutateAsync` que, quando chamada, executa a exclusão.
 *
 * @returns A função `mutateAsync` do `react-query`.
 */
export const useDeleteTimesheetDayMutation = () => {
  const { mutateAsync } = useMutation({
    mutationFn: async (data: TimesheetDay) => {
      if (!data || !data.id) throw new Error("TimesheetDay ID inválido");
      return await timesheetDayRepository.delete(data.id);
    },
  });
  return mutateAsync;
};
